import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiData from '../../services/api_data';

export const getEbooks = createAsyncThunk(
    'ebook/getEbooks',
    async (clientId, { rejectWithValue }) => {
        try {
            const data = await apiData.getEbooks(clientId);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getEbookById = createAsyncThunk(
    'ebook/getEbookById',
    async (ebookId, { rejectWithValue }) => {
        try {
            const data = await apiData.getEbookById(ebookId);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const createEbook = createAsyncThunk(
    'ebook/createEbook',
    async (ebookData, { rejectWithValue }) => {
        try {
            const data = await apiData.createEbook(ebookData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateEbook = createAsyncThunk(
    'ebook/updateEbook',
    async ({ id, ebookData }, { rejectWithValue }) => {
        try {
            const data = await apiData.updateEbook(id, ebookData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteEbook = createAsyncThunk(
    'ebook/deleteEbook',
    async (ebookId, { rejectWithValue }) => {
        try {
            await apiData.deleteEbook(ebookId);
            return ebookId;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const addItemsToEbook = createAsyncThunk(
    'ebook/addItems',
    async ({ ebookId, items }, { rejectWithValue }) => {
        try {
            const data = await apiData.addEbookItems(ebookId, items);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const removeItemFromEbook = createAsyncThunk(
    'ebook/removeItem',
    async ({ ebookId, dataId }, { rejectWithValue }) => {
        try {
            await apiData.removeEbookItem(ebookId, dataId);
            return { dataId };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const reorderEbookItems = createAsyncThunk(
    'ebook/reorderItems',
    async ({ ebookId, items }, { rejectWithValue }) => {
        try {
            await apiData.reorderEbookItems(ebookId, items);
            return items;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const ebookSlice = createSlice({
    name: 'ebook',
    initialState: {
        ebooks: [],
        currentEbook: null,
        loading: false,
        error: null,
    },
    reducers: {
        clearCurrentEbook: (state) => {
            state.currentEbook = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        updateCurrentEbookItems: (state, action) => {
            if (state.currentEbook) {
                state.currentEbook.ebookItems = action.payload;
            }
        },
        setShareStatus: (state, action) => {
            if (state.currentEbook) {
                state.currentEbook.shareEnabled = action.payload.shareEnabled;
                state.currentEbook.publicUrl = action.payload.publicUrl;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getEbooks.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getEbooks.fulfilled, (state, action) => {
            state.loading = false;
            state.ebooks = action.payload;
        });
        builder.addCase(getEbooks.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(getEbookById.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getEbookById.fulfilled, (state, action) => {
            state.loading = false;
            state.currentEbook = action.payload;
        });
        builder.addCase(getEbookById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(createEbook.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(createEbook.fulfilled, (state, action) => {
            state.loading = false;
            state.currentEbook = action.payload;
            state.ebooks.push(action.payload);
        });
        builder.addCase(createEbook.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(updateEbook.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(updateEbook.fulfilled, (state, action) => {
            state.loading = false;
            state.currentEbook = action.payload;
            const index = state.ebooks.findIndex(e => e.id === action.payload.id);
            if (index !== -1) state.ebooks[index] = action.payload;
        });
        builder.addCase(updateEbook.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        builder.addCase(deleteEbook.fulfilled, (state, action) => {
            state.ebooks = state.ebooks.filter(e => e.id !== action.payload);
            if (state.currentEbook?.id === action.payload) state.currentEbook = null;
        });

        builder.addCase(addItemsToEbook.fulfilled, (state, action) => {
            state.currentEbook = action.payload;
        });
    },
});

export const { clearCurrentEbook, clearError, updateCurrentEbookItems, setShareStatus } = ebookSlice.actions;
export default ebookSlice.reducer;
